import { GraphQuery, Risk } from '@/types';

/**
 * WIRE-FORMAT CONTRACT — `IN` filters on scalar node fields (ENG-5257 / ENG-3855)
 *
 * An `IN` filter value MUST be sent NESTED — `value: [[a, b, c]]` — never flat.
 * The backend deserializes the field as `filters.SliceOrValue[T]`, trying `[]T`
 * first, so a flat array becomes N separate filters and each one binds its `$pN`
 * to a scalar string. Neo4j then rejects the compiled Cypher at plan time
 * ("Type mismatch for parameter 'pN': expected List<T> but was String"), the
 * error is swallowed, and the UI silently receives HTTP 200 with an empty tree.
 * The nested form compiles to a single `field IN $p1` bound to a real list.
 *
 * Do NOT "simplify" any of these back to a flat array — regression-tested in
 * `__tests__/queries.test.tsx`. This applies only to filter VALUES; relationship
 * `label` lists (see createADObjectsQuery) are a different field and stay flat.
 */

/**
 * Creates a GraphQuery for fetching asset-to-seed relationships.
 * Returns all seeds that discovered the given assets.
 *
 * @returns GraphQuery for ASSET -> DISCOVERED -> SEED relationships
 */
export const createAssetToSeedQuery = (): GraphQuery => ({
  node: {
    labels: ['Asset'],
    filters: [],
    relationships: [
      {
        label: 'DISCOVERED',
        target: {
          labels: ['Seed'],
          filters: [],
        },
        length: -1,
      },
    ],
  },
});

/**
 * Creates a GraphQuery for fetching asset-to-risk relationships.
 * Filters by specific asset keys to only return risks for those assets.
 *
 * @param assetKeys - Array of asset keys to filter by
 * @returns GraphQuery for RISK <- HAS_VULNERABILITY <- ASSET relationships
 */
export const createAssetToRiskQuery = (assetKeys: string[]): GraphQuery => ({
  node: {
    labels: ['Risk'],
    filters: [],
    relationships: [
      {
        label: 'HAS_VULNERABILITY',
        source: {
          labels: ['Asset'],
          filters: [
            {
              field: 'key',
              operator: 'IN',
              // Nested — see the wire-format contract at the top of this file.
              value: [assetKeys],
            },
          ],
          relationships: [],
        },
        optional: false,
      },
    ],
  },
  limit: 101,
  page: 0,
});

/**
 * Creates a GraphQuery for fetching technologies associated with assets.
 * Traverses ASSET -> HAS_ATTRIBUTE -> ATTRIBUTE -> HAS_TECHNOLOGY -> TECHNOLOGY.
 *
 * @param assetKeys - Array of asset keys to filter by
 * @returns GraphQuery for asset-to-technology relationships
 */
export const createAssetToTechnologyQuery = (
  assetKeys: string[]
): GraphQuery => ({
  node: {
    labels: ['Asset'],
    filters: [
      {
        field: 'key',
        operator: 'IN',
        // Nested — see the wire-format contract at the top of this file.
        value: [assetKeys],
      },
    ],
    relationships: [
      {
        label: 'HAS_ATTRIBUTE',
        target: {
          labels: ['Attribute'],
          filters: [],
          relationships: [
            {
              label: 'HAS_TECHNOLOGY',
              target: {
                labels: ['Technology'],
                filters: [],
                relationships: [],
              },
            },
          ],
        },
        optional: false,
      },
    ],
  },
  limit: 1001,
  page: 0,
});

/**
 * Creates a GraphQuery for fetching webpages discovered by attributes.
 * Returns WEBPAGE <- DISCOVERED <- ATTRIBUTE relationships.
 *
 * @returns GraphQuery for webpage-to-attribute relationships
 */
export const createWebpageToAttributeQuery = (): GraphQuery => ({
  node: {
    labels: ['Webpage'],
    relationships: [
      {
        label: 'DISCOVERED',
        source: {
          labels: ['Attribute'],
        },
      },
    ],
  },
  limit: 500,
  page: 0,
});

/**
 * Creates a GraphQuery for fetching assets associated with specific risks.
 * Filters by risk keys and returns assets with HAS_VULNERABILITY relationships.
 *
 * @param risks - Array of risks to find associated assets for
 * @returns GraphQuery for risk-to-asset relationships
 */
export const createRiskToAssetQuery = (risks: Risk[]): GraphQuery => ({
  node: {
    labels: ['Asset'],
    filters: [{ field: 'status', operator: '=', value: 'D', not: true }],
    relationships: [
      {
        label: 'HAS_VULNERABILITY',
        target: {
          labels: ['Risk'],
          filters: [
            {
              field: 'key',
              operator: 'IN',
              // Nested — see the wire-format contract at the top of this file.
              value: [risks.map(risk => risk.key)],
            },
          ],
        },
      },
    ],
  },
});

/**
 * Creates a GraphQuery for fetching risk tags and vulnerability relationships.
 * Returns risks with their INSTANCE_OF relationships to vulnerabilities.
 *
 * @returns GraphQuery for risk-to-vulnerability relationships
 */
export const createRiskTagsQuery = (): GraphQuery => ({
  node: {
    labels: ['Risk'],
    relationships: [
      {
        label: 'INSTANCE_OF',
        target: { labels: ['Vulnerability'], sorted: false },
        optional: true,
      },
    ],
  },
  orderBy: 'priority',
  descending: false,
  limit: 1000,
  page: 0,
});

/**
 * Creates a GraphQuery for fetching asset tags.
 * Returns assets with their INSTANCE_OF relationships.
 *
 * @returns GraphQuery for asset tag relationships
 */
export const createAssetTagsQuery = (): GraphQuery => ({
  node: {
    labels: ['Asset'],
    relationships: [
      {
        label: 'INSTANCE_OF',
        target: { labels: ['Asset'], sorted: false },
        optional: true,
      },
    ],
  },
  limit: 1000,
  page: 0,
});

/**
 * Creates a GraphQuery for fetching AD (Active Directory) objects and their relationships.
 * Returns Asset nodes with AD classes and all AD relationship types (MemberOf, AdminTo, etc.).
 *
 * @returns GraphQuery for SharpHound AD object relationships
 */
export const createADObjectsQuery = (): GraphQuery => ({
  node: {
    labels: ['Asset'],
    filters: [
      {
        field: 'class',
        operator: 'IN',
        // Nested — see the wire-format contract at the top of this file.
        value: [
          ['aduser', 'adcomputer', 'adgroup', 'addomain', 'adou', 'adgpo'],
        ],
      },
    ],
    relationships: [
      {
        // Use array of all AD relationship types that SharpHound creates
        label: [
          'MemberOf',
          'AdminTo',
          'CanRDP',
          'CanPSRemote',
          'HasSession',
          'GenericAll',
          'GenericWrite',
          'Owns',
          'WriteDacl',
          'WriteOwner',
          'ForceChangePassword',
          'AllExtendedRights',
          'AddMember',
          'Contains',
          'GPLink',
          'AllowedToDelegate',
          'AllowedToAct',
          'ExecuteDCOM',
          'HasSIDHistory',
          'DCSync',
          'ReadLAPSPassword',
          'ReadGMSAPassword',
          'SQLAdmin',
          'WriteSPN',
          'AddKeyCredentialLink',
          'GetChanges',
          'GetChangesAll',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ] as any, // Cast to any since AD relationships aren't in the Relationship type
        target: {
          labels: ['Asset'],
          filters: [
            {
              field: 'class',
              operator: 'IN',
              // Nested — see the wire-format contract at the top of this file.
              value: [
                [
                  'aduser',
                  'adcomputer',
                  'adgroup',
                  'addomain',
                  'adou',
                  'adgpo',
                ],
              ],
            },
          ],
        },
        optional: true, // Make relationships optional so nodes without relationships still show
      },
    ],
  },
});
